// Fax Manager — the agency-wide view of the Faxage pipeline. Faxes that
// couldn't be matched to a child sit at the top until a coordinator files
// them; below that, recent traffic in both directions. "Check now" runs the
// hourly ingest immediately.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFaxInbox, runFaxIngest } from '../../lib/faxes';
import { listFaxDrafts } from '../../lib/faxDrafts';
import FaxDraftRow from '../../components/faxes/FaxDraftRow';
import FaxRow from '../../components/faxes/FaxRow';
import AssignFaxModal from '../../components/faxes/AssignFaxModal';
import SendFaxModal from '../../components/faxes/SendFaxModal';

const FILTERS = [
  { key: 'review',   label: 'Needs review' },
  { key: 'inbound',  label: 'Received' },
  { key: 'outbound', label: 'Sent' },
  { key: 'open',     label: 'Awaiting reply' },
  { key: 'drafts',   label: 'Drafts' },
  { key: 'all',      label: 'All' },
];

// The list keeps itself current — a cheap database read, nothing to do with
// Faxage — while the page is visible. Talking to Faxage stays on the hourly
// job and the one button.
const RELOAD_MS = 30_000;

export default function FaxInbox() {
  const navigate = useNavigate();
  const [faxes, setFaxes] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('review');
  const [assigning, setAssigning] = useState(null);
  const [checking, setChecking] = useState(false);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data }, { data: d }] = await Promise.all([getFaxInbox(), listFaxDrafts()]);
    setFaxes(data || []);
    setDrafts(d || []);
    setLoading(false);
  }, []);
  // A plain fax draft reopens here. A Request documents draft reopens on the
  // child's Faxes tab, where the window has the child's Drive folder to hand.
  const resumeDraft = (draft) => {
    if (draft.kind === 'request' && draft.client_id) navigate(`/clients/${draft.client_id}?tab=faxes&draft=${draft.id}`);
    else setSending(draft);
  };
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') load(); };
    const id = setInterval(tick, RELOAD_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [load]);

  // Runs the ingest and keeps going while Faxage still has more waiting —
  // each call handles a handful, so the first drain of a backlog takes a few.
  const checkNow = async () => {
    setChecking(true); setNote('');
    const tally = { processed: 0, filed: 0, review: 0, errs: 0, outbound: 0 };
    let folderWarning = '';
    try {
      for (let pass = 0; pass < 40; pass++) {
        const r = await runFaxIngest();
        if (Array.isArray(r.portal_folders) && r.portal_folders.length === 0) folderWarning = ' · Faxage folders not visible to Caera_Fax — tick it under the padlock on Faxage → folders.';
        else if (r.portal_folders?.error) folderWarning = ` · Faxage folder list failed: ${r.portal_folders.error}`;
        const rs = r.results || [];
        tally.processed += r.processed || 0;
        tally.filed += rs.filter(x => x.status === 'matched').length;
        tally.review += rs.filter(x => x.status === 'unmatched').length;
        tally.errs += rs.filter(x => x.status === 'error').length;
        tally.outbound += Math.max(0, r.outbound_updated || 0);
        if (r.remaining > 0) { setNote(`Working… ${tally.processed} done, ${r.remaining} still waiting at Faxage.`); await load(); continue; }
        break;
      }
      setNote(tally.processed === 0 && tally.outbound === 0
        ? `Nothing new at Faxage.${folderWarning}`
        : `Processed ${tally.processed}: ${tally.filed} filed, ${tally.review} to review${tally.errs ? `, ${tally.errs} failed (will retry)` : ''}${tally.outbound ? ` · ${tally.outbound} sent fax${tally.outbound === 1 ? '' : 'es'} updated` : ''}.${folderWarning}`);
      await load();
    } catch (e) { setNote(e.message || 'Check failed.'); await load(); }
    finally { setChecking(false); }
  };

  const counts = useMemo(() => ({
    review:   faxes.filter(f => f.direction === 'inbound' && ['unmatched', 'error'].includes(f.status)).length,
    inbound:  faxes.filter(f => f.direction === 'inbound').length,
    outbound: faxes.filter(f => f.direction === 'outbound').length,
    open:     faxes.filter(f => f.direction === 'outbound' && ['pending', 'success'].includes(f.status) && f.request_items
                && Object.keys(f.request_items).some(k => f.request_items[k] && !(f.fulfilled_items || {})[k])).length,
    drafts:   drafts.length,
    all:      faxes.length,
  }), [faxes, drafts]);

  const shown = useMemo(() => faxes.filter(f => {
    if (filter === 'review')   return f.direction === 'inbound' && ['unmatched', 'error'].includes(f.status);
    if (filter === 'inbound')  return f.direction === 'inbound';
    if (filter === 'outbound') return f.direction === 'outbound';
    if (filter === 'open')     return f.direction === 'outbound' && ['pending', 'success'].includes(f.status) && f.request_items
                                 && Object.keys(f.request_items).some(k => f.request_items[k] && !(f.fulfilled_items || {})[k]);
    return true;
  }), [faxes, filter]);

  return (
    <div style={{ padding: 4 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer', fontSize: 13, background: filter === f.key ? 'var(--accent)' : 'var(--card-bg)', color: filter === f.key ? '#fff' : 'var(--text)' }}>
            {f.label}{counts[f.key] ? <span style={{ marginLeft: 6, opacity: 0.8 }}>{counts[f.key]}</span> : null}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {note && <span style={{ fontSize: 12, color: 'var(--text3)' }}>{note}</span>}
          <button className="btn btn-outline btn-sm" onClick={() => setSending(true)} title="Any files to any number, with a cover page — about a client or not">Send a fax</button>
          <button className="btn btn-primary btn-sm" onClick={checkNow} disabled={checking} title="Pull new faxes and delivery results from Faxage right now instead of waiting for the hourly run">{checking ? 'Checking Faxage…' : 'Check Faxage now'}</button>
        </div>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 12px' }}>
        Faxes are pulled from Faxage every hour, read, filed into the child's Drive folder under <b>Faxes</b>, and announced in Chat. This list updates itself. Anything that couldn't be matched to a client confidently waits here under <b>Needs review</b> — file it and the request checklist updates.
      </p>

      <div className="card" style={{ border: '0.5px solid var(--accent-border)' }}>
        <div style={{ padding: '4px 20px' }}>
          {loading && faxes.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', padding: '16px 0' }}>Loading…</div>}
          {filter === 'drafts' ? (
            <>
              {!loading && drafts.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--text3)', padding: '16px 0' }}>
                  No drafts. Anything you start in Send a fax or Request documents is saved here automatically until it's sent.
                </div>
              )}
              {drafts.map((d, i) => <FaxDraftRow key={d.id} draft={d} showClient last={i === drafts.length - 1} onResume={resumeDraft} onChanged={load} />)}
            </>
          ) : (
            <>
              {!loading && shown.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--text3)', padding: '16px 0' }}>
                  {filter === 'review' ? 'Nothing waiting for review.' : 'Nothing here yet.'}
                </div>
              )}
              {shown.map((f, i) => <FaxRow key={f.id} fax={f} showClient last={i === shown.length - 1} onAssign={setAssigning} onChanged={load} />)}
            </>
          )}
        </div>
      </div>

      {assigning && <AssignFaxModal fax={assigning} onClose={() => setAssigning(null)} onDone={load} />}
      {sending && <SendFaxModal draft={sending === true ? null : sending} onClose={() => { setSending(false); load(); }} onSent={() => { setSending(false); load(); setNote('Fax queued.'); }} />}
    </div>
  );
}
